import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next'; 
import { 
  Save, ArrowLeft, Search, FileText, History, Paperclip, 
  ChevronLeft, ChevronRight as ChevronRightIcon, Plus, Minus,
  Layers, RefreshCw, Calendar, BarChart3, Activity, 
  Clock, Trash2, Box, ShieldCheck, Zap, Info, Filter, List
} from 'lucide-react';

import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import { useFontSize } from './FontSizeContext';
import { usePermissions } from './usePermissions';

const PMDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  const [activeTab, setActiveTab] = useState('PM');
  const [loading, setLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [count, setCount] = useState(0); 
  const [selectedSeqIdx, setSelectedSeqIdx] = useState(null);

  const [nav, setNav] = useState({ first: null, last: null, next: null, prev: null, total: 0, current_pos: 0 });

  const { canCreate, canUpdate, canDelete } = usePermissions('pm');
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  
  const [data, setData] = useState({
    pmnum: '', description: '', status: 'DRAFT', 
    site_id: session.site_id || '', org_id: session.org_id || '',
    assetnum: '', asset_loc: '', location: '', jpnum: '', worktype: 'PM', 
    wostatus: 'WSCH', priority: 0, supervisor: '', glaccount: '', 
    routenum: '', counter: 0, laststartdate: '', lastcompdate: '', erlstwogendate: '',
    targstarttime: '1970-01-01T08:00:00', forecastexist: false,
    saturday: true, sunday: true, monday: true, tuesday: true, wednesday: true, thursday: true, friday: true,
    frequencies: [{ frequency: 0, frequnit: 'DAYS', usetargetdate: true }],
    meters: [], sequences: [], forecasts: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null });
  const isReadOnly = ['ACTIVE', 'INACTIVE'].includes(data.status);
  const [forecastPage, setForecastPage] = useState(1);
  const [totalForecasts, setTotalForecasts] = useState(0);
  const forecastLimit = 20;
  const [compliance, setCompliance] = useState({
      last_start_date: null, last_completion_date: null, next_due_date: null, has_forecast: false
  });

  const loadForecasts = useCallback(async () => {
      if (isNew || activeTab !== 'Forecast') return;
      try {
          const res = await axios.get(`/api/pm/${id}/forecasts`, { params: { page: forecastPage, limit: forecastLimit } });
          setData(prev => ({ ...prev, forecasts: Array.isArray(res.data.data) ? res.data.data : [] }));
          setTotalForecasts(res.data.total || 0);
      } catch (err) { console.error("Forecast load failed"); }
  }, [id, isNew, activeTab, forecastPage]);

  useEffect(() => { loadForecasts(); }, [loadForecasts]);

  const fetchComplianceSummary = useCallback(async () => {
      if (isNew) return;
      try {
          const res = await axios.get(`/api/pm/${id}/compliance-summary`);
          setCompliance(res.data);
      } catch (err) { console.error("Error fetching compliance summary", err); }
  }, [id, isNew]);

  useEffect(() => { fetchComplianceSummary(); }, [fetchComplianceSummary, data.forecasts]);

  const loadData = useCallback(async () => {
    if (!isNew) {
      setLoading(true);
      try {
        const res = await axios.get(`/api/pm/${id}`);
        const safeData = {
          ...res.data,
          meters: Array.isArray(res.data.meters) ? res.data.meters : [],
          sequences: Array.isArray(res.data.sequences) ? res.data.sequences : [],
          forecasts: Array.isArray(res.data.forecasts) ? res.data.forecasts : [],
          frequencies: Array.isArray(res.data.frequencies) ? res.data.frequencies : [{ frequency: 0, frequnit: 'DAYS', usetargetdate: true }]
        };
        setData(safeData);
      } catch (err) { toast.error(t('common.loadError')); } 
      finally { setLoading(false); }
    }
  }, [id, isNew, t]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (isNew && data.org_id) {
        axios.get(`/api/pm/next-number/${data.org_id}`)
            .then(res => setData(prev => ({ ...prev, pmnum: res.data.next_pmnum })));
    }
  }, [isNew, data.org_id]);

  const fetchCount = useCallback(async () => {
    if (!data.pmnum || isNew) {
        setCount(0);
        return;
    }
    try {
      const res = await axios.get(`/api/attachments/count/PM/${data.pmnum}`);
      setCount(res.data.count || 0);
    } catch (err) { 
        console.error("Error fetching attachment count:", err); 
    }
  }, [data.pmnum, isNew]);  

  useEffect(() => {
    fetchCount();
  }, [data.pmnum, fetchCount]);

  const loadNavigation = useCallback(async () => {
      if (isNew) return;
      const searchParams = new URLSearchParams(location.search);
      try {
          const res = await axios.get(`/api/pm/${id}/navigation`, {
              params: {
                  all_sites: searchParams.get('all_sites') || "false",
                  q: searchParams.get('q') || "",
                  status: searchParams.get('status') || "",
                  assetnum: searchParams.get('assetnum') || "",
                  location_id: searchParams.get('location_id') || "",
                  sortBy: searchParams.get('sortBy') || "pmnum",
                  sortDir: searchParams.get('sortDir') || "desc",
                  site_id: searchParams.get('user_siteid') || "",
                  org_id: searchParams.get('org_id') || "",
                  user_siteid: session.site_id,
                  orgid: session.org_id
              },
              headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          setNav(res.data);
      } catch (err) { console.error("Navigation system failed to sync"); }
  }, [id, isNew, location.search, session.site_id, session.org_id]);

  useEffect(() => { loadNavigation(); }, [loadNavigation]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const res = await axios.post('/api/pm/save', data, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success(t('common.save_success'));
      if (isNew) navigate(`/pm/${res.data.pmnum}${location.search}`);
    } catch (err) { toast.error(t('common.save_fail')); } 
    finally { setLoading(false); }
  };

  const handleForecast = async () => {
    const tid = toast.loading(t('pm.messages.executingForecast'));
    try {
      await axios.post(`/api/pm/${data.pmnum}/reforecast`);
      toast.success(t('pm.messages.forecastSuccess'), { id: tid });
      loadData();
    } catch (err) { toast.error(t('pm.messages.forecastFail'), { id: tid }); }
  };

  // 🎯 دوال إدارة المصفوفات (لضمان إضافة الصفوف بدون مشاكل)
  const addMeterRow = () => {
    setData(prev => ({ ...prev, meters: [...(prev.meters || []), { meternum: '', frequency: 0 }] }));
  };

  const updateMeterRow = (idx, field, value) => {
    setData(prev => {
      const newMeters = [...(prev.meters || [])];
      newMeters[idx] = { ...newMeters[idx], [field]: value };
      return { ...prev, meters: newMeters };
    });
  };

  const removeMeterRow = (idx) => {
    setData(prev => ({ ...prev, meters: (prev.meters || []).filter((_, i) => i !== idx) }));
  };

  const addSequenceRow = () => {
    setData(prev => {
      const currentSeqs = Array.isArray(prev.sequences) ? prev.sequences : [];
      return { ...prev, sequences: [...currentSeqs, { jpnum: '', interval: currentSeqs.length + 1 }] };
    });
  };

  const updateSequenceRow = (idx, field, value) => {
    setData(prev => {
      const newSeqs = [...(prev.sequences || [])];
      newSeqs[idx] = { ...newSeqs[idx], [field]: value };
      return { ...prev, sequences: newSeqs };
    });
  };

  const removeSequenceRow = (idx) => {
    setData(prev => ({ ...prev, sequences: (prev.sequences || []).filter((_, i) => i !== idx) }));
  };

  const handleSearch = (field, title, type, idx = null) => {
    setLookup({ isOpen: true, title, type, field, filters: { site_id: data.site_id, org_id: data.org_id }, targetIdx: idx });
  };

  const handleLookupSelect = (row) => {
    // 🎯 شبكة اصطياد القيم: بتدور على أي اسم محتمل جي من الباك إند
    const val = row.value || row.id || row.assetnum || row.jpnum || row.jp_num || row.location_id || row.meternum || row.meter_num;
    
    if (lookup.field === 'assetnum') {
        setData(prev => ({ 
          ...prev, 
          assetnum: row.assetnum || val, 
          asset_loc: row.location_id || prev.asset_loc, 
          location: row.location || prev.location 
        }));
    } 
    else if (lookup.field === 'sequences') {
        // تحديث حقل الـ Job Plan جوه سطر التسلسل
        updateSequenceRow(lookup.targetIdx, 'jpnum', row.jpnum || row.jp_num || val);
    }
    else if (lookup.field === 'meters') {
        // تحديث حقل العداد جوه سطر العدادات
        console.log("Selected Meter Row:", row); // للتأكد من البيانات
        updateMeterRow(lookup.targetIdx, 'meternum', row.meternum || row.meter_num || val);
    }
    else { 
        setData(prev => ({ ...prev, [lookup.field]: val })); 
    }
    
    // قفل اللوكاب بعد الاختيار
    setLookup(p => ({ ...p, isOpen: false }));
  };

  useEffect(() => {
    if (isNew || !data.pmnum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const openStatusModal = () => {
        setShowStatusModal(true);
    };
    window.addEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
    return () => window.removeEventListener('GLOBAL_STATUS_HANDLER', openStatusModal);
  }, [isNew, data.pmnum]);

  useEffect(() => {
    if (isNew || !data.pmnum) {
        console.log("⚠️ Document is new, skipping SideMenu trigger.");
        return;
    }
    const handleOpenHistory = () => {
      setShowHistoryModal(true);
    };
    window.addEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    return () => {
      window.removeEventListener('OPEN_STATUS_HISTORY', handleOpenHistory);
    };
  }, [isNew, data.pmnum]);

  const goTo = (targetId) => { if (targetId) navigate(`/pm/${targetId}${location.search}`); };

  return (
    <div className={`flex flex-col h-full bg-[#f3f4f6] text-[11px] uppercase tracking-tighter ${isRTL ? 'font-cairo dir-rtl' : 'font-sans'}`} style={{ fontSize: `${fontSize}px` }}>
      <UniversalLookup {...lookup} onClose={() => setLookup(p => ({ ...p, isOpen: false }))} onSelect={handleLookupSelect} />
      
      {showStatusModal && <StatusModal currentStatus={data.status} recordId={data.pmnum} appName="PM" onClose={() => setShowStatusModal(false)} onComplete={loadData} />}
      {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="PM" recordId={data.pmnum} onClose={() => setShowHistoryModal(false)} />}
      {isDocModalOpen && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); loadData();}} ownertable="PM" ownerid={data.pmnum} />}

      {/* --- TOOLBAR --- */}
      <div className="bg-[#1a3353] text-white p-1.5 flex justify-between items-center shadow-lg px-4 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/pm${location.search}`)} className="p-1.5 hover:bg-white/10 rounded-full transition-all active:scale-90">
            <ArrowLeft size={18} className={isRTL ? 'rotate-180' : ''} />
          </button>
          
          {!isNew && (
            <div className="flex items-center bg-black/30 border border-white/10 rounded px-1 py-0.5 gap-1 font-mono shadow-inner">
                <button onClick={() => goTo(nav.first)} className="p-1 hover:text-orange-400 disabled:opacity-20 transition-colors" title={t('common.first')}><ChevronLeft size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
                <button onClick={() => goTo(nav.prev)} className="p-1 hover:text-orange-400 disabled:opacity-20 transition-colors" title={t('common.prev')}><ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <span className="px-3 text-orange-400 font-black border-x border-white/10 tracking-widest">{nav.current_pos} / {nav.total}</span>
                <button onClick={() => goTo(nav.next)} className="p-1 hover:text-orange-400 disabled:opacity-20 transition-colors" title={t('common.next')}><ChevronRightIcon size={16} className={isRTL ? 'rotate-180' : ''}/></button>
                <button onClick={() => goTo(nav.last)} className="p-1 hover:text-orange-400 disabled:opacity-20 transition-colors" title={t('common.last')}><ChevronRightIcon size={16} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/></button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          {!isNew && (
            <button onClick={handleForecast} className="bg-[#fb8c00] hover:bg-orange-600 px-5 py-1.5 rounded-sm flex items-center gap-2 font-black shadow-md transition-all active:scale-95">
                <BarChart3 size={14}/> {t('pm.actions.forecast')}
            </button>
          )}
          
          <button onClick={handleSave} disabled={loading || isReadOnly} className="bg-green-600 hover:bg-green-700 px-6 py-1.5 rounded-sm flex items-center gap-2 font-black shadow-md transition-all active:scale-95 disabled:opacity-50">
            <Save size={14}/> {loading ? t('common.saving') : t('pm.actions.savePM')}
          </button>
          
          <div className="h-6 w-px bg-white/20 mx-2"></div>

          <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-blue-200 hover:text-white transition-colors" title={t('common.attachments')}>
            <Paperclip size={18}/> 
            {count > 0 && <span className="absolute top-1 right-1 bg-red-600 text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center font-black">{count}</span>}
          </button>
          
          <button onClick={() => setShowHistoryModal(true)} className="p-2 text-blue-200 hover:text-white transition-colors" title={t('common.history')}>
            <History size={18}/>
          </button>

          <div className="flex items-center gap-2 bg-white/10 px-3 py-1 rounded-full border border-white/10 shadow-inner ml-4">
            <button onClick={() => fontContext.changeFontSize(-1)} className="hover:text-orange-400 transition-colors"><Minus size={14} /></button>
            <span className="font-black text-[10px] text-blue-200 min-w-[15px] text-center">{fontSize}</span>
            <button onClick={() => fontContext.changeFontSize(1)} className="hover:text-orange-400 transition-colors"><Plus size={14} /></button>
          </div>
        </div>
      </div>

      {/* --- HEADER --- */}
      <div className="bg-white border-b px-6 py-4 flex items-center gap-12 shadow-sm shrink-0">
        <HeaderField label={t('pm.fields.pmNum')} value={data.pmnum} isBold width="w-36" disabled />
        <HeaderField 
            label={t('pm.fields.description')} 
            value={data.description} 
            onChange={v => setData(prev => ({...prev, description: v}))} 
            width="w-[500px]" 
            disabled={isReadOnly} 
            placeholder={t('pm.placeholders.desc')}
        />
        <HeaderField 
            label={t('pm.fields.status')} 
            value={data.status} 
            disabled 
            width="w-28" 
            isStatus 
            onClick={() => setShowStatusModal(true)} 
        />
        <HeaderField label={t('pm.fields.site')} value={data.site_id} disabled width="w-28" />
      </div>

      {/* --- TABS NAVIGATION --- */}
      <div className="flex bg-white border-b px-4 shrink-0 font-black uppercase tracking-widest shadow-sm">
        {['PM', 'Frequency', 'Sequence', 'Seasonal Dates', 'Forecast'].map(tKey => (
          <button 
            key={tKey} 
            onClick={() => setActiveTab(tKey)} 
            className={`px-10 py-3.5 transition-all text-[10px] border-b-4 ${activeTab === tKey ? 'border-[#1a3353] text-[#1a3353] bg-gray-50' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
          >
            {t(`pm.tabs.${tKey.replace(' ', '').toLowerCase()}`)}
          </button>
        ))}
      </div>

      {/* --- MAIN CONTENT AREA --- */}
      <div className="flex-1 overflow-y-auto p-6 bg-[#f8f9fb] custom-scrollbar">
        
        {/* TAB 1: PM MAIN DATA */}
        {activeTab === 'PM' && (
          <div className="grid grid-cols-12 gap-8 max-w-7xl mx-auto pb-24 animate-in fade-in slide-in-from-top-2 duration-500">
            <div className="col-span-8 space-y-8">
              <SectionCard title={t('pm.sections.assetLoc')}>
                <div className="grid grid-cols-2 gap-x-16 gap-y-6">
                  <EditableField label={t('pm.fields.asset')} value={data.assetnum} onSearch={() => handleSearch('assetnum', t('common.search'), 'assets')} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.location')} value={data.asset_loc} onSearch={() => handleSearch('asset_loc', t('common.search'), 'locations')} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.route')} value={data.routenum} onChange={v => setData(prev => ({...prev, routenum: v}))} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.counter')} value={data.counter} type="number" onChange={v => setData(prev => ({...prev, counter: v}))} disabled={isReadOnly} />
                </div>
              </SectionCard>

              <SectionCard title={t('pm.sections.defaults')}>
                <div className="grid grid-cols-2 gap-x-16 gap-y-6">
                  <EditableField label={t('pm.fields.jpnum')} value={data.jpnum} onSearch={() => handleSearch('jpnum', t('common.search'), 'jobplans')} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.worktype')} value={data.worktype} onChange={v => setData(prev => ({...prev, worktype: v}))} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.supervisor')} value={data.supervisor} onSearch={() => handleSearch('supervisor', t('common.search'), 'labors')} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.glaccount')} value={data.glaccount} onChange={v => setData(prev => ({...prev, glaccount: v}))} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.woStatus')} value={data.wostatus} onChange={v => setData(prev => ({...prev, wostatus: v}))} disabled={isReadOnly} />
                  <EditableField label={t('pm.fields.priority')} value={data.priority} type="number" onChange={v => setData(prev => ({...prev, priority: v}))} disabled={isReadOnly} />
                </div>
              </SectionCard>
            </div>

            <div className="col-span-4 space-y-8">
              <SectionCard title={t('pm.sections.compliance')}>
                 <div className="space-y-5">
                    <StaticField label={t('pm.fields.lastStart')} value={compliance.last_start_date?.split('T')[0] || t('common.pending')} icon={<Clock size={12}/>} />
                    <StaticField label={t('pm.fields.lastComp')} value={compliance.last_completion_date?.split('T')[0] || t('common.pending')} />
                    <StaticField label={t('pm.fields.nextDue')} value={compliance.next_due_date?.split('T')[0] || t('common.pending')} isRed icon={<Activity size={12}/>} />
                    <div className="p-4 bg-blue-50/50 border border-blue-100 rounded flex items-center justify-between">
                        <span className="font-black text-blue-900 text-[9px] uppercase tracking-widest italic">{t('pm.fields.forecastFlag')}</span>
                        <div className={`w-3 h-3 rounded-full shadow-lg ${data.forecastexist ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`}></div>
                    </div>
                 </div>
              </SectionCard>

              <div className="bg-[#1a3353] p-6 rounded shadow-2xl text-white/90 space-y-4">
                  <div className="flex items-center gap-2 border-b border-white/10 pb-2">
                    <ShieldCheck size={18} className="text-orange-400"/>
                    <span className="font-black uppercase tracking-widest text-[10px]">{t('pm.sections.siteContext')}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                     <div>
                        <label className="text-[8px] opacity-40 uppercase block mb-1 font-bold italic">{t('pm.fields.org')}</label>
                        <span className="font-black text-[12px] tracking-widest">{data.org_id}</span>
                     </div>
                     <div>
                        <label className="text-[8px] opacity-40 uppercase block mb-1 font-bold italic">{t('pm.fields.site')}</label>
                        <span className="font-black text-[12px] tracking-widest">{data.site_id}</span>
                     </div>
                  </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: FREQUENCY */}
        {activeTab === 'Frequency' && (
           <div className="max-w-7xl mx-auto space-y-8 animate-in slide-in-from-bottom duration-500 pb-24">
              <SectionCard title={t('pm.sections.timeBased')}>
                <div className="grid grid-cols-3 gap-20">
                   <EditableField 
                        label={t('pm.fields.freqInt')} 
                        type="number" 
                        value={data.frequencies?.[0]?.frequency || 0} 
                        onChange={v => { 
                            setData(prev => {
                                const f = Array.isArray(prev.frequencies) ? [...prev.frequencies] : [];
                                if(!f[0]) f[0] = { frequency: 0, frequnit: 'DAYS', usetargetdate: true }; 
                                f[0].frequency = v; 
                                return { ...prev, frequencies: f };
                            });
                        }} 
                   />
                  <div className="flex flex-col gap-1 border-b border-gray-200 py-1 px-1">
                    <label className="text-gray-400 font-black text-[8px] uppercase tracking-widest italic">{t('pm.fields.freqUnit')}</label>
                    <select 
                      value={data.frequencies?.[0]?.frequnit || 'DAYS'} 
                      onChange={(e) => { 
                        const val = e.target.value; 
                        setData(prev => {
                            const f = Array.isArray(prev.frequencies) ? [...prev.frequencies] : [];
                            if(!f[0]) f[0] = { frequency: 0, frequnit: 'DAYS', usetargetdate: true }; 
                            f[0].frequnit = val; 
                            return { ...prev, frequencies: f };
                        });
                      }} 
                      className="outline-none font-black text-blue-900 bg-transparent py-0.5 text-[11px] uppercase cursor-pointer"
                    >
                      <option value="DAYS">{t('common.days')}</option>
                      <option value="WEEKS">{t('common.weeks')}</option>
                      <option value="MONTHS">{t('common.months')}</option>
                      <option value="YEARS">{t('common.years')}</option>
                    </select>
                  </div>
                   <div className="flex flex-col gap-1 border-b border-gray-200 py-1 px-1">
                      <label className="text-gray-400 font-black text-[8px] uppercase tracking-widest italic">{t('pm.fields.targStart')}</label>
                      <input 
                        type="time" 
                        className="outline-none font-black text-blue-900 bg-transparent py-0.5 text-[0.9]" 
                        value={data.targstarttime ? data.targstarttime.split('T')[1]?.substring(0,5) : '08:00'} 
                        onChange={e => setData(prev => ({...prev, targstarttime: `1970-01-01T${e.target.value}:00`}))} 
                      />
                   </div>
                </div>
              </SectionCard>

              {/* Meter Table */}
              <div className="bg-white border rounded shadow-xl overflow-hidden min-h-[350px]">
                 <div className="p-3 bg-slate-50 border-b font-black text-[10px] text-slate-500 uppercase tracking-[0.2em] flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-3 bg-blue-600 rounded-full shadow-sm"></div> {t('pm.sections.meters')}
                    </div>
                    <button 
                        onClick={addMeterRow} 
                        className="bg-blue-900 text-white px-6 py-1.5 rounded-sm font-black text-[9px] hover:bg-black transition-all shadow-md active:scale-95 uppercase"
                    >
                        {t('pm.actions.addMeter')}
                    </button>
                 </div>
                 <table className="w-full text-left">
                    <thead className="bg-slate-50 border-b text-gray-400 font-black uppercase text-[8px] tracking-widest">
                       <tr>
                           <th className="p-3 border-x w-12 text-center">#</th>
                           <th className="p-3 border-x">{t('pm.tables.meterName')}</th>
                           <th className="p-3 border-x text-center w-40">{t('pm.tables.freq')}</th>
                           <th className="p-3 text-center w-48 bg-orange-50/30 text-orange-600 italic">{t('pm.tables.nextProj')}</th>
                           <th className="p-3 w-10"></th>
                       </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                       {(data.meters || []).map((m, i) => (
                          <tr key={i} className="hover:bg-blue-50/50 group transition-all" onClick={() => setSelectedSeqIdx(i)}>
                             <td className="p-3 border-x text-center text-gray-300 font-mono text-[9px]">{i+1}</td>
                             <td className="p-3 border-x font-black text-blue-900">
                                <div className="flex justify-between items-center">
                                  <span>{m.meternum || t('common.pendingSelection')}</span>
                                  <Search size={14} className="text-blue-400 cursor-pointer opacity-0 group-hover:opacity-100" onClick={() => handleSearch('meters', t('common.search'), 'meters', i)} />
                                </div>
                             </td>
                             <td className="p-3 border-x text-center">
                                <input className="w-full text-center outline-none bg-transparent font-black text-blue-900" value={m.frequency} type="number" onChange={e => updateMeterRow(i, 'frequency', e.target.value)} />
                             </td>
                             <td className="p-3 text-center text-orange-700 font-black italic tracking-widest bg-orange-50/20">{m.readingnextwo || '0.00'}</td>
                             <td className="p-3 text-center">
                                <Trash2 size={14} className="text-gray-300 hover:text-red-500 cursor-pointer" onClick={() => removeMeterRow(i)} />
                             </td>
                          </tr>
                       ))}
                       {(!data.meters || data.meters.length === 0) && (
                          <tr><td colSpan="5" className="p-20 text-center text-gray-300 italic uppercase tracking-widest font-black text-[10px]">{t('pm.messages.noMeters')}</td></tr>
                       )}
                    </tbody>
                 </table>
              </div>
           </div>
        )}

        {/* TAB 3: SEQUENCE */}
        {activeTab === 'Sequence' && (
           <div className="max-w-5xl mx-auto space-y-6 animate-in slide-in-from-right duration-500 pb-24">
               <div className="bg-white border rounded shadow-2xl overflow-hidden">
                    <div className="p-3 bg-slate-100 border-b flex justify-between items-center">
                       <h3 className="font-black text-[#1a3353] text-[10px] flex items-center gap-2 tracking-[0.2em] uppercase italic">
                         <Layers size={14} className="text-blue-600"/> {t('pm.sections.sequences')}
                       </h3>
                       <button 
                        onClick={addSequenceRow} 
                        className="bg-blue-900 text-white px-8 py-2 rounded-sm font-black text-[9px] flex items-center gap-2 hover:bg-black transition-all uppercase"
                       >
                        <Plus size={14}/> {t('pm.actions.addStep')}
                       </button>
                    </div>
                    <table className="w-full text-left">
                       <thead className="bg-[#f9fafb] text-gray-400 text-[8px] font-black uppercase border-b tracking-widest">
                          <tr>
                             <th className="p-3 border-x text-center w-32 italic">{t('pm.tables.seq')}</th>
                             <th className="p-3 border-x">{t('pm.tables.jpnum')}</th>
                             <th className="p-3 text-center w-12"></th>
                          </tr>
                       </thead>
                       <tbody className="divide-y font-bold">
                          {(data.sequences || []).map((s, i) => (
                            <tr key={i} className="hover:bg-blue-50 transition-colors">
                               <td className="p-3 border-x text-center">
                                  <input className="w-full text-center outline-none bg-transparent font-black text-blue-900" value={s.interval} onChange={e => updateSequenceRow(i, 'interval', e.target.value)} />
                               </td>
                               <td className="p-3 border-x font-black text-blue-900">
                                  <div className="flex justify-between items-center">
                                     <span className="tracking-widest uppercase">{s.jpnum || t('common.select')}</span>
                                     <Search size={14} className="text-blue-400 cursor-pointer" onClick={() => handleSearch('sequences', t('common.search'), 'jobplans', i)} />
                                  </div>
                               </td>
                               <td className="p-3 text-center">
                                  <Trash2 size={14} className="text-gray-300 hover:text-red-600 cursor-pointer" onClick={() => removeSequenceRow(i)} />
                               </td>
                            </tr>
                          ))}
                          {(!data.sequences || data.sequences.length === 0) && (
                            <tr><td colSpan="3" className="p-16 text-center text-gray-300 italic uppercase font-black text-[9px] tracking-[0.4em]">{t('pm.messages.noSeq')}</td></tr>
                          )}
                       </tbody>
                    </table>
               </div>
           </div>
        )}

        {/* TAB 4: SEASONAL DATES */}
        {activeTab === 'Seasonal Dates' && (
           <div className="max-w-4xl mx-auto animate-in zoom-in-95 duration-500 pb-24">
               <SectionCard title={t('pm.sections.seasonal')}>
                  <div className="bg-blue-50 p-4 rounded border border-blue-100 mb-6 flex items-center gap-4">
                     <Info size={24} className="text-blue-600 shadow-sm shrink-0" />
                     <p className="text-[10px] font-black text-blue-900 uppercase italic leading-tight">
                        {t('pm.messages.seasonalInfo')}
                     </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                     {['saturday', 'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map(day => (
                        <div key={day} className="flex items-center justify-between p-4 bg-white border border-gray-100 rounded shadow-sm hover:border-blue-400 transition-all group">
                           <span className="font-black text-blue-900 text-[11px] uppercase tracking-tighter italic group-hover:text-blue-600 transition-colors">{t(`common.daysList.${day}`)}</span>
                           <input 
                                type="checkbox" 
                                className="w-5 h-5 accent-blue-900 cursor-pointer shadow-inner" 
                                checked={data[day]} 
                                onChange={e => setData(prev => ({...prev, [day]: e.target.checked}))} 
                           />
                        </div>
                     ))}
                  </div>
               </SectionCard>
           </div>
        )}

        {/* TAB 5: FORECAST LIST */}
        {activeTab === 'Forecast' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-left duration-500 pb-24">
            <div className="bg-white border rounded shadow-2xl overflow-hidden min-h-[500px] flex flex-col">
              <div className="p-3 bg-[#1a3353] text-white flex justify-between items-center shadow-md">
                <div className="flex items-center gap-3">
                    <BarChart3 size={16} className="text-orange-400" />
                    <span className="font-black tracking-[0.2em] uppercase text-[10px]">{t('pm.sections.projections')}</span>
                </div>
                <span className="text-[9px] bg-white/10 px-3 py-1 rounded-full font-black border border-white/5 uppercase">
                    {t('common.totalRecords')}: {totalForecasts}
                </span>
              </div>
              
              <div className="flex-1 overflow-auto custom-scrollbar">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-[#f3f4f6] text-gray-500 text-[9px] font-black uppercase tracking-widest sticky top-0 z-10 border-b shadow-sm">
                    <tr>
                      <th className="p-4 border-x w-44 italic">{t('pm.tables.fcDate')}</th>
                      <th className="p-4 border-x">{t('pm.tables.targetJp')}</th>
                      <th className="p-4 border-x text-center w-40">{t('pm.tables.estCost')}</th>
                      <th className="p-4 border-x text-center w-32">{t('pm.tables.status')}</th>
                      <th className="p-4 text-center w-44">{t('pm.tables.woNum')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y font-bold">
                    {(data.forecasts || []).map((f, idx) => (
                      <tr key={idx} className="hover:bg-blue-50/50 transition-all group">
                        <td className="p-4 border-x font-black text-blue-900 text-[12px] italic tracking-tighter">{f.forecastdate?.split('T')[0]}</td>
                        <td className="p-4 border-x font-black text-blue-700 tracking-widest uppercase">{f.jpnum}</td>
                        <td className="p-4 border-x text-center font-black text-green-700 bg-green-50/10">
                          {f.cost?.total_est_cost?.toLocaleString(undefined, {minimumFractionDigits: 2}) || '0.00'}
                        </td>
                        <td className="p-4 border-x text-center">
                          <span className={`px-4 py-1 rounded-sm text-[8px] font-black shadow-sm ${f.isgenerated ? 'bg-green-600 text-white' : 'bg-blue-100 text-blue-400 border border-blue-200'}`}>
                              {f.isgenerated ? t('pm.tables.generated') : t('pm.tables.projected')}
                          </span>
                        </td>
                        <td className="p-4 text-center text-blue-900 font-black text-[12px] cursor-pointer hover:text-orange-600 underline decoration-blue-100 transition-colors" onClick={() => f.wonum && navigate(`/workorders/${f.wonum}`)}>
                          {f.wonum || '---'}
                        </td>
                      </tr>
                    ))}
                    {(!data.forecasts || data.forecasts.length === 0) && (
                      <tr><td colSpan="5" className="p-10 text-center text-gray-400 italic font-black uppercase text-[10px]">{t('pm.messages.noForecasts')}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Forecast Pagination Footer */}
              <div className="p-3 bg-slate-50 border-t flex justify-between items-center px-10">
                <div className="text-[10px] font-black text-slate-400 uppercase italic">
                  {t('pm.messages.showingProjections', { count: data.forecasts?.length || 0 })}
                </div>
                <div className="flex items-center gap-3">
                  <button 
                    disabled={forecastPage === 1}
                    onClick={() => setForecastPage(p => p - 1)}
                    className="p-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-20 transition-all active:scale-90"
                  >
                      <ChevronLeft size={16} className={isRTL ? 'rotate-180' : ''}/>
                  </button>
                  <span className="font-mono text-[11px] font-black bg-white px-4 py-1 rounded border shadow-inner text-blue-900">
                      {forecastPage} / {Math.ceil(totalForecasts / forecastLimit) || 1}
                  </span>
                  <button 
                    disabled={forecastPage >= Math.ceil(totalForecasts / forecastLimit)}
                    onClick={() => setForecastPage(p => p + 1)}
                    className="p-1.5 border border-gray-300 rounded hover:bg-white disabled:opacity-20 transition-all active:scale-90"
                  >
                      <ChevronRightIcon size={16} className={isRTL ? 'rotate-180' : ''}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* --- FOOTER --- */}
      <div className="bg-[#1a3353] text-white p-2 px-10 flex justify-between items-center shadow-inner shrink-0 z-50">
          <div className="flex items-center gap-10 text-[9px] font-black uppercase tracking-[0.25em]">
             <div className="flex items-center gap-2">
                <ShieldCheck size={14} className="text-orange-400"/>
                <span>Auth: <span className="text-orange-300 tracking-widest">{session.username}</span></span>
             </div>
             <div className="flex items-center gap-2 border-l border-white/10 pl-10">
                <Box size={14} className="text-blue-400"/>
                <span>Site Node: <span className="text-blue-300 tracking-widest">{data.site_id} / {data.org_id}</span></span>
             </div>
          </div>
          <div className="flex items-center gap-5">
             <div className="font-mono text-[9px] text-gray-500 italic">REF_NODE: {data.pmnum || 'NEW'}</div>
             <div className="w-2.5 h-2.5 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(34,197,94,0.6)]"></div>
          </div>
      </div>
    </div>
  );
};

// --- المكونات المساعدة ---

const HeaderField = ({ label, value, onChange, width, isBold, disabled, isStatus, onClick, placeholder }) => (
  <div className="flex flex-col px-5 border-r border-gray-100 last:border-0">
    <label className="text-[0.8em] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <div className={`flex items-center gap-2 ${isStatus ? 'cursor-pointer hover:bg-gray-50 transition-colors p-1 rounded-sm' : ''}`} onClick={onClick}>
      {isStatus && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse shadow-md shadow-green-200"></span>}
      <input 
        className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[13px] tracking-tighter' : 'text-blue-700 text-[12px]'} bg-transparent transition-all focus:border-blue-500 placeholder:font-normal placeholder:lowercase placeholder:italic`} 
        placeholder={placeholder}
        value={value || ''} 
        disabled={disabled} 
        onChange={e => onChange && onChange(e.target.value)} 
      />
    </div>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden transition-all hover:shadow-md border-gray-200">
    <div className="p-2.5 bg-slate-50 border-b font-black text-slate-500 uppercase text-[0.9em] tracking-widest flex items-center gap-2 italic">
      <div className="w-1.5 h-4 bg-orange-600 rounded-full shadow-sm"></div> {title}
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
  <div className="flex flex-col gap-1 border-b border-gray-200 group focus-within:border-blue-500 transition-all py-1.5 px-1 bg-transparent">
    <label className="text-gray-400 font-black text-[0.8em] uppercase tracking-widest italic leading-none">{label}</label>
    <div className="flex items-center justify-between pb-0.5 h-6">
      <input 
        type={type} 
        className="outline-none font-black text-blue-900 bg-transparent w-full text-[12px] tracking-tighter placeholder:font-normal placeholder:text-gray-300" 
        value={value || ''} 
        disabled={disabled} 
        onChange={e => onChange && onChange(e.target.value)} 
      />
      {onSearch && (
        <Search 
            size={14} 
            className="text-blue-300 cursor-pointer hover:text-blue-800 transition-all hover:scale-110 active:scale-90" 
            onClick={onSearch} 
        />
      )}
    </div>
  </div>
);

const StaticField = ({ label, value, isRed, icon }) => (
  <div className="flex flex-col border-b border-gray-100 py-2 px-1 bg-white/40 shadow-inner rounded-sm mb-1">
    <div className="flex items-center gap-2 mb-1">
       {icon && <span className="text-blue-300">{icon}</span>}
       <label className="text-[0.8em] font-black text-gray-400 uppercase tracking-widest italic">{label}</label>
    </div>
    <div className={`font-black uppercase tracking-[0.1em] text-[12px] mt-0.5 ${isRed ? 'text-red-600 animate-pulse' : 'text-blue-900'} italic`}>
        {value || '--- PENDING ---'}
    </div>
  </div>
);

export default PMDetails;